/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
import { css } from '@firebolt-dev/css'
import { useCallback, useEffect, useRef, useState } from 'react'

import { COLORS, MOTION, RADII, SHADOWS, TYPE, space } from '../theme'
import { EMOTES, emoteWheel, screenData, subscribeScreenData } from '../screen-data'
import { radialIndex } from '../emote-wheel-state'

const ERROR_MESSAGES = {
  INVALID_PAYLOAD: 'Действие отклонено сервером.',
  IDEMPOTENCY_CONFLICT: 'Повтор запроса не совпал с исходным действием.',
  RATE_LIMITED: 'Слишком много действий. Немного подожди.',
  AVATAR_UNAVAILABLE: 'Текущий персонаж не поддерживает эту анимацию.',
  UNKNOWN_EMOTE: 'Этой эмоции больше нет в каталоге.',
  LOCKED: 'Эта эмоция не разблокирована.',
  COOLDOWN: 'Эмоция ещё восстанавливается.',
}

export function EmoteWheelScreen({ world, onClose }) {
  const [state, setState] = useState(() => emoteWheel(world))
  const [loaded, setLoaded] = useState(() => screenData(world, 'emotes') !== null)
  const [selected, setSelected] = useState(0)
  const lastResult = useRef(null)
  const previousFocus = useRef(null)
  const root = useRef(null)

  useEffect(() => {
    previousFocus.current = document.activeElement
    root.current?.focus()
    const off = subscribeScreenData(world, 'emotes', () => {
      const next = emoteWheel(world)
      setState(next)
      setLoaded(true)
      setSelected(index => Math.min(index, Math.max(0, next.catalog.length - 1)))
      const result = next.lastResult
      if (result?.requestId && result.requestId !== lastResult.current) {
        lastResult.current = result.requestId
        if (!result.ok) world.ui.notify({
          kind: result.code === 'COOLDOWN' || result.code === 'RATE_LIMITED' ? 'warning' : 'error',
          title: 'ЭМОЦИЯ НЕ ЗАПУЩЕНА',
          message: ERROR_MESSAGES[result.code] || 'Сервер отклонил действие.',
          dedupeKey: `emote:${result.code || 'error'}`,
        })
      }
    })
    EMOTES.refresh(world)
    return () => {
      off()
      previousFocus.current?.focus?.()
    }
  }, [world])

  const choose = useCallback(index => {
    const item = state.catalog[index]
    if (!item) return
    const cooldown = Number(state.cooldowns[item.id] || 0)
    if (cooldown > 0) {
      world.ui.notify({ kind: 'warning', title: 'ВОССТАНОВЛЕНИЕ', message: `Подожди ${Math.ceil(cooldown / 1000)} сек.`, dedupeKey: `emote:cooldown:${item.id}` })
      return
    }
    if (!EMOTES.start(world, item.id)) world.ui.notify({ kind: 'error', title: 'НЕТ СОЕДИНЕНИЯ', message: 'Действие не отправлено в мир.', dedupeKey: 'emote:offline' })
  }, [state, world])

  useEffect(() => {
    const key = event => {
      if (event.key === 'Escape') { event.preventDefault(); onClose?.(); return }
      if (!state.catalog.length) return
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { event.preventDefault(); setSelected(value => (value + 1) % state.catalog.length) }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { event.preventDefault(); setSelected(value => (value - 1 + state.catalog.length) % state.catalog.length) }
      if (event.key === 'Enter' || event.code === 'Space') { event.preventDefault(); choose(selected) }
    }
    const release = event => {
      if (event.code === 'KeyG') { event.preventDefault(); choose(selected); onClose?.() }
    }
    window.addEventListener('keydown', key)
    window.addEventListener('keyup', release)
    return () => { window.removeEventListener('keydown', key); window.removeEventListener('keyup', release) }
  }, [choose, onClose, selected, state.catalog.length])

  useEffect(() => {
    let frame
    let previousButtons = []
    const poll = () => {
      const pad = navigator.getGamepads?.()?.find(Boolean)
      if (pad) {
        const index = radialIndex(pad.axes?.[0] || 0, pad.axes?.[1] || 0, state.catalog.length)
        if (index >= 0) setSelected(index)
        const pressed = pad.buttons?.map(button => button.pressed) || []
        if (pressed[0] && !previousButtons[0]) choose(index >= 0 ? index : selected)
        if (pressed[1] && !previousButtons[1]) onClose?.()
        previousButtons = pressed
      }
      frame = requestAnimationFrame(poll)
    }
    frame = requestAnimationFrame(poll)
    return () => cancelAnimationFrame(frame)
  }, [choose, onClose, selected, state.catalog.length])

  const activeId = state.active?.id
  return (
    <section
      ref={root}
      tabIndex={-1}
      role='dialog'
      aria-modal='false'
      aria-label='Колесо эмоций'
      css={css`
        position: fixed; inset: auto 50% 88px auto; transform: translateX(50%); z-index: 45;
        width: min(92vw, 620px); padding: ${space(2)}; color: ${COLORS.text};
        background: ${COLORS.bgPanel}; border: 1px solid ${COLORS.borderStrong}; border-radius: ${RADII.xxl};
        box-shadow: ${SHADOWS.panel}; backdrop-filter: blur(24px); outline: none;
        @media (max-width: 600px) { inset: 72px ${space(1)} 84px; transform:none; width:auto; overflow:auto; }
      `}
    >
      <header css={css`display:flex;justify-content:space-between;align-items:start;gap:${space(2)};margin-bottom:${space(2)};`}>
        <div><div css={css`font-size:${TYPE.section.size};font-weight:${TYPE.section.weight};letter-spacing:${TYPE.section.spacing};color:${COLORS.cyan};`}>ЭМОЦИИ / ПОЗЫ</div><div css={css`margin-top:4px;color:${COLORS.textSecondary};font-size:${TYPE.caption.size};`}>G удерживать · стрелки/стик выбрать · Enter/A запустить · Esc/B отменить</div></div>
        <button type='button' aria-label='Закрыть колесо эмоций' onClick={onClose} css={css`min-width:44px;min-height:44px;border:1px solid ${COLORS.border};border-radius:${RADII.md};background:${COLORS.bgPanelLight};color:${COLORS.text};`}>×</button>
      </header>

      {!loaded ? <div role='status' css={css`padding:${space(4)};text-align:center;color:${COLORS.textSecondary};`}>Загрузка каталога мира…</div> :
        !state.available ? <div role='alert' css={css`padding:${space(4)};text-align:center;color:${COLORS.warning};`}>Анимации недоступны для текущего персонажа.</div> :
        !state.catalog.length ? <div role='status' css={css`padding:${space(4)};text-align:center;color:${COLORS.textSecondary};`}>В мире пока нет доступных лицензированных эмоций.</div> : (
          <div css={css`display:grid;grid-template-columns:minmax(240px,1fr) minmax(180px,.7fr);gap:${space(2)};@media(max-width:600px){grid-template-columns:1fr;}`}>
            <div role='listbox' aria-label='Радиальный выбор эмоции' aria-activedescendant={`emote-wheel-${state.catalog[selected]?.id}`} css={css`position:relative;width:min(58vw,300px);aspect-ratio:1;margin:auto;border:1px solid ${COLORS.border};border-radius:50%;background:radial-gradient(circle,${COLORS.cyanSoft} 0 20%,transparent 21%),${COLORS.bgDeep};`}>
              {state.catalog.map((item, index) => {
                const angle = index * Math.PI * 2 / state.catalog.length - Math.PI / 2
                const left = 50 + Math.cos(angle) * 36
                const top = 50 + Math.sin(angle) * 36
                const cooldown = Number(state.cooldowns[item.id] || 0)
                return <button id={`emote-wheel-${item.id}`} role='option' aria-selected={selected === index} key={item.id} type='button' disabled={cooldown > 0} onFocus={() => setSelected(index)} onPointerEnter={() => setSelected(index)} onClick={() => choose(index)} css={css`
                  position:absolute;left:${left}%;top:${top}%;transform:translate(-50%,-50%);min-width:92px;min-height:48px;padding:8px;
                  border:1px solid ${selected === index ? COLORS.cyan : COLORS.border};border-radius:${RADII.pill};background:${selected === index ? COLORS.cyanSoft : COLORS.bgElevated};
                  color:${COLORS.text};transition:${MOTION.hover};box-shadow:${selected === index ? SHADOWS.glowCyan : 'none'};
                `}>{item.label}{cooldown > 0 ? ` · ${Math.ceil(cooldown / 1000)}с` : ''}</button>
              })}
              <div aria-hidden='true' css={css`position:absolute;inset:42%;display:grid;place-items:center;color:${COLORS.cyan};font-size:11px;text-align:center;`}>{activeId ? 'АКТИВНО' : 'ВЫБОР'}</div>
            </div>

            <div><div css={css`font-size:${TYPE.caption.size};letter-spacing:.16em;color:${COLORS.textSecondary};margin-bottom:${space(1)};`}>ДОСТУПНЫЙ СПИСОК</div><div role='list' css={css`display:grid;gap:8px;`}>
              {state.catalog.map((item, index) => <button role='listitem' key={item.id} type='button' onClick={() => { setSelected(index); choose(index) }} css={css`min-height:44px;text-align:left;padding:0 12px;border:1px solid ${activeId === item.id ? COLORS.success : COLORS.border};border-radius:${RADII.md};background:${selected === index ? COLORS.cyanSoft : COLORS.bgPanelLight};color:${COLORS.text};`}><strong>{item.label}</strong><span css={css`display:block;font-size:11px;color:${COLORS.textSecondary};`}>{item.category === 'pose' ? 'Поза' : item.category}</span></button>)}
            </div>{activeId && <button type='button' onClick={() => EMOTES.stop(world)} css={css`width:100%;min-height:44px;margin-top:${space(2)};border:1px solid ${COLORS.warning};border-radius:${RADII.md};background:${COLORS.warningSoft};color:${COLORS.text};`}>ОСТАНОВИТЬ</button>}</div>
          </div>
        )}
    </section>
  )
}
